import { skipToken, useSuspenseQuery } from '@tanstack/react-query';
import { useContext } from 'react';

import type { IntegrationsBlocksSet, SpaceIntegrationBlocks } from '@gitbook/api-client';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { apiQuery, apiResource, readAPICache } from '../../../state';
import { ContentEditorContext } from '../contexts';

/**
 * Get all integration blocks installed and available on the current space by reading the API cache.
 */
export function useSpaceIntegrationsBlocksFromCache() {
    const { integrationBlocksContext } = useContext(ContentEditorContext);
    return useEventCallback(() => {
        if (!integrationBlocksContext) {
            return getIntegrationBlocksSetFromSpaceIntegrationBlocks(null);
        }
        const integrationsBlocks = readAPICache(
            apiResource('listSpaceIntegrationsBlocks', {
                spaceId: integrationBlocksContext.spaceId,
            })
        );
        return getIntegrationBlocksSetFromSpaceIntegrationBlocks(integrationsBlocks);
    });
}

/**
 * Fetch all integration blocks installed and available on the current space
 */
export function useSpaceIntegrationsBlocks() {
    const { integrationBlocksContext } = useContext(ContentEditorContext);
    const { data: integrationsBlocks } = useSuspenseQuery(
        apiQuery(
            'listSpaceIntegrationsBlocks',
            integrationBlocksContext ? { spaceId: integrationBlocksContext.spaceId } : skipToken
        )
    );
    return getIntegrationBlocksSetFromSpaceIntegrationBlocks(integrationsBlocks);
}

/**
 * Transform a list of space integration blocks into a set of integration blocks.
 */
function getIntegrationBlocksSetFromSpaceIntegrationBlocks(
    integrationsBlocks: SpaceIntegrationBlocks | null
) {
    if (!integrationsBlocks) {
        return {} as IntegrationsBlocksSet;
    }
    const integrationsBlocksSet: IntegrationsBlocksSet = {};
    integrationsBlocks.forEach((integration) => {
        integrationsBlocksSet[integration.name] = integration.blocks;
    });

    return integrationsBlocksSet;
}
